import { useEffect, useMemo, useRef, useState } from "react";
import SectionTitleIcon from "../menu/SectionTitleIcon";
import SgcFiltrosHeader from "../filtros/SgcFiltrosHeader";
import SgcFiltroSection from "../filtros/SgcFiltroSection";
import SgcFiltrosFooter from "../filtros/SgcFiltrosFooter";
import "../filtros/SgcFiltrosSidebar.css";
import "./VolcanDocumentos.css";

function linkifyUrls(text) {
  const s = text == null ? "" : String(text);
  if (!s.trim()) return s;

  const urlRe = /(https?:\/\/[^\s<>()\]}]+[^\s<>().,;:"'!?()[\]}])/g;
  const parts = s.split(urlRe);
  if (parts.length === 1) return s;

  return parts.map((part, idx) => {
    if (!part) return null;
    if (!/^https?:\/\//i.test(part)) return <span key={idx}>{part}</span>;
    return (
      <a
        key={idx}
        href={part}
        className="doc-link"
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    );
  });
}

function extractYearFromReferencia(value) {
  if (value == null) return null;
  const s = String(value);
  if (!s.trim()) return null;

  const m1 = s.match(/\((1[5-9]\d{2}|20\d{2}|21\d{2})\)/);
  if (m1) return Number(m1[1]);

  const m2 = s.match(/\b(1[5-9]\d{2}|20\d{2}|21\d{2})\b/);
  if (m2) return Number(m2[1]);

  return null;
}

function rowMatchesQuery(doc, q) {
  if (!q) return true;
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const tip = String(doc?.tipologia ?? "").toLowerCase();
  const ref = String(doc?.referencia_bibliografica ?? "").toLowerCase();
  const id = String(doc?.id ?? "");
  return tip.includes(n) || ref.includes(n) || id.includes(n);
}

function BibliografiaColgroup() {
  return (
    <colgroup>
      <col className="doc-biblio-col-num" style={{ width: "70px" }} />
      <col className="doc-biblio-col-tip" style={{ width: "16%" }} />
      <col />
    </colgroup>
  );
}

function VolcanDocumentos({ documentos }) {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return !window.matchMedia("(max-width: 720px)").matches;
  });
  const [yearFrom, setYearFrom] = useState(null);
  const [yearTo, setYearTo] = useState(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const tableEl = tableRef.current;
    if (!tableEl) return;

    const scrollParent = tableEl.closest(".volcan-body-scroll");
    if (!scrollParent) return;

    const firstTh = tableEl.querySelector("thead th");
    if (!firstTh) return;

    const applyStickyTop = () => {
      const titleEl = document.querySelector(".volcan-title--sticky");
      const bodyInnerEl = document.querySelector(".volcan-body-inner");

      const titleH = titleEl ? titleEl.getBoundingClientRect().height : 0;
      const bodyPadTop = bodyInnerEl
        ? parseFloat(window.getComputedStyle(bodyInnerEl).paddingTop || "0")
        : 0;
      const stickyTopPx = Math.max(0, Math.round(titleH + bodyPadTop));
      const maskTopPx = Math.max(0, Math.round(titleH));
      // Un pequeño extra evita que se cuele texto por subpixeles al hacer scroll.
      const maskHpx = Math.max(0, Math.round(bodyPadTop + 4));

      scrollParent.style.setProperty(
        "--doc-biblio-sticky-top",
        `${stickyTopPx}px`,
      );
      scrollParent.style.setProperty("--doc-biblio-mask-top", `${maskTopPx}px`);
      scrollParent.style.setProperty("--doc-biblio-mask-h", `${maskHpx}px`);
    };

    const updateStuck = () => {
      const stickyTop = parseFloat(
        window
          .getComputedStyle(scrollParent)
          .getPropertyValue("--doc-biblio-sticky-top") || "0",
      );
      const parentTop = scrollParent.getBoundingClientRect().top;
      const thTop = firstTh.getBoundingClientRect().top;
      const isStuck = thTop <= parentTop + stickyTop + 1;
      scrollParent.toggleAttribute("data-doc-biblio-stuck", isStuck);
    };

    applyStickyTop();
    window.addEventListener("resize", applyStickyTop);
    scrollParent.addEventListener("scroll", updateStuck, { passive: true });
    updateStuck();

    return () => {
      window.removeEventListener("resize", applyStickyTop);
      scrollParent.removeEventListener("scroll", updateStuck);
    };
  }, []);

  const yearsMeta = useMemo(() => {
    const list = documentos || [];
    const years = list
      .map((d) => extractYearFromReferencia(d?.referencia_bibliografica))
      .filter((y) => Number.isFinite(y));
    if (years.length === 0) {
      return { minYear: null, maxYear: null };
    }
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    return { minYear, maxYear };
  }, [documentos]);

  const minYear = yearsMeta.minYear;
  const maxYear = yearsMeta.maxYear;

  useEffect(() => {
    if (!minYear || !maxYear) return;
    setYearFrom((prev) => {
      if (prev == null || Number(prev) === 0) return minYear;
      return Math.min(Math.max(Number(prev), minYear), maxYear);
    });
    setYearTo((prev) => {
      if (prev == null || Number(prev) === 0) return maxYear;
      return Math.min(Math.max(Number(prev), minYear), maxYear);
    });
  }, [minYear, maxYear]);

  const filteredDocs = useMemo(() => {
    const list = documentos || [];
    const yFrom = minYear && yearFrom != null ? Number(yearFrom) : null;
    const yTo = maxYear && yearTo != null ? Number(yearTo) : null;
    const yMin = yFrom != null && yTo != null ? Math.min(yFrom, yTo) : yFrom;
    const yMax = yFrom != null && yTo != null ? Math.max(yFrom, yTo) : yTo;

    return list.filter((doc) => {
      if (!rowMatchesQuery(doc, searchQuery)) return false;

      if (!minYear || !maxYear || yMin == null || yMax == null) return true;
      const y = extractYearFromReferencia(doc?.referencia_bibliografica);
      if (!Number.isFinite(y)) return true;
      return y >= yMin && y <= yMax;
    });
  }, [documentos, searchQuery, minYear, maxYear, yearFrom, yearTo]);

  const sortedData = useMemo(() => {
    const base = filteredDocs.map((doc, originalIndex) => ({
      ...doc,
      __originalIndex: originalIndex,
    }));

    if (!sortConfig.key) return base;

    const sorted = [...base].sort((a, b) => {
      if (sortConfig.key === "__index") {
        return sortConfig.direction === "asc"
          ? a.__originalIndex - b.__originalIndex
          : b.__originalIndex - a.__originalIndex;
      }

      const aValue = String(a[sortConfig.key] ?? "").toLowerCase();
      const bValue = String(b[sortConfig.key] ?? "").toLowerCase();

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredDocs, sortConfig]);

  useEffect(() => {
    if (selectedDocId == null) return;
    const exists = sortedData.some(
      (d) => String(d?.id) === String(selectedDocId),
    );
    if (!exists) setSelectedDocId(null);
  }, [sortedData, selectedDocId]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getArrow = (key) => {
    if (sortConfig.key !== key) return " ↕";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const theadRow = (
    <tr>
      <th onClick={() => handleSort("__index")}>
        <div className="th-content">
          <span>#</span>
        </div>
      </th>
      <th onClick={() => handleSort("tipologia")}>
        <div className="th-content">
          <span>Tipología</span>
          <span>{getArrow("tipologia")}</span>
        </div>
      </th>
      <th onClick={() => handleSort("referencia_bibliografica")}>
        <div className="th-content">
          <span>Referencia bibliográfica</span>
          <span>{getArrow("referencia_bibliografica")}</span>
        </div>
      </th>
    </tr>
  );

  const tbodyContent =
    sortedData.length === 0 ? (
      <tr>
        <td colSpan={3} className="documentos-empty-filter">
          No hay resultados para “{searchQuery.trim()}”. Pruebe otras palabras o
          limpie el buscador.
        </td>
      </tr>
    ) : (
      sortedData.map((doc, index) => (
        <tr
          key={doc.id ?? `${doc.__originalIndex}-${index}`}
          className={
            selectedDocId != null && String(doc?.id) === String(selectedDocId)
              ? "is-selected"
              : ""
          }
          onClick={() => {
            if (doc?.id == null) return;
            setSelectedDocId(doc.id);
          }}
        >
          <td data-label="#">{index + 1}</td>
          <td data-label="Tipología">{doc.tipologia}</td>
          <td data-label="Referencia bibliográfica">
            {linkifyUrls(doc.referencia_bibliografica)}
          </td>
        </tr>
      ))
    );

  const hasDocs = (documentos || []).length > 0;

  const clearFilters = () => {
    setSearchQuery("");
    if (minYear && maxYear) {
      setYearFrom(minYear);
      setYearTo(maxYear);
    } else {
      setYearFrom(null);
      setYearTo(null);
    }
    setSortConfig({ key: null, direction: "asc" });
  };

  return (
    <>
      {hasDocs ? (
        <div className="documentos-section">
          <div
            className={`sgc-section-shell${filtersOpen ? "" : " is-filters-collapsed"}`}
          >
            {filtersOpen ? (
              <aside
                className="sgc-filtros sgc-filtros--section-sidebar"
                aria-label="Filtros"
              >
                <SgcFiltrosHeader onCollapse={() => setFiltersOpen(false)} />

                <div className="sgc-filtros__body">
                  <SgcFiltroSection icon="search" label="Búsqueda">
                    <label
                      className="sgc-filtro-search"
                      htmlFor="documentos-search"
                    >
                      <i className="bi bi-search" aria-hidden />
                      <input
                        id="documentos-search"
                        type="search"
                        placeholder="Tipología, referencia, texto…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoComplete="off"
                        spellCheck="false"
                      />
                    </label>
                  </SgcFiltroSection>

                  <SgcFiltroSection icon="calendar3" label="Rango de años">
                    <div className="doc-years">
                      <label className="doc-year-field">
                        <span>Desde</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={yearFrom ?? ""}
                          min={minYear || undefined}
                          max={maxYear || undefined}
                          placeholder={minYear ? String(minYear) : "0000"}
                          onChange={(e) =>
                            setYearFrom(Number(e.target.value || 0))
                          }
                        />
                      </label>
                      <label className="doc-year-field">
                        <span>Hasta</span>
                        <input
                          type="number"
                          inputMode="numeric"
                          value={yearTo ?? ""}
                          min={minYear || undefined}
                          max={maxYear || undefined}
                          placeholder={maxYear ? String(maxYear) : "0000"}
                          onChange={(e) =>
                            setYearTo(Number(e.target.value || 0))
                          }
                        />
                      </label>
                    </div>

                    {minYear && maxYear ? (
                      <div className="doc-range" aria-hidden="true">
                        <div className="doc-range-track" />
                        <input
                          type="range"
                          min={minYear}
                          max={maxYear}
                          value={Math.min(
                            Number(yearFrom) || minYear,
                            Number(yearTo) || maxYear,
                          )}
                          onChange={(e) => setYearFrom(Number(e.target.value))}
                        />
                        <input
                          type="range"
                          min={minYear}
                          max={maxYear}
                          value={Math.max(
                            Number(yearFrom) || minYear,
                            Number(yearTo) || maxYear,
                          )}
                          onChange={(e) => setYearTo(Number(e.target.value))}
                        />
                      </div>
                    ) : (
                      <div className="doc-years-empty">
                        No hay año en las referencias.
                      </div>
                    )}
                  </SgcFiltroSection>
                </div>

                <SgcFiltrosFooter
                  summary={
                    <>
                      Mostrando <strong>{sortedData.length}</strong> documentos
                    </>
                  }
                  onClear={clearFilters}
                />
              </aside>
            ) : null}

            <div className="sgc-section-content sgc-filtros-main">
              <div className="sgc-section-title">
                <div className="sgc-section-title__main">
                  <SectionTitleIcon sectionKey="documentos" />
                  <div className="sgc-section-title__text">
                    <div className="sgc-section-title__h">Documentos</div>
                    <div className="sgc-section-title__p">
                      Listado de fuentes y documentos consultados sobre la
                      historia del volcán.
                    </div>
                  </div>
                </div>
                {!filtersOpen ? (
                  <button
                    type="button"
                    className="sgc-filtros-open"
                    onClick={() => setFiltersOpen(true)}
                    aria-label="Expandir filtros"
                    data-sgc-tooltip="Mostrar filtros"
                  >
                    <i className="bi bi-funnel" aria-hidden />{" "}
                    <span>Filtros</span>
                  </button>
                ) : null}
              </div>

              <div className="tabla-container tabla-container--bibliografia-doc">
                <div className="doc-biblio-desktop">
                  <div className="doc-biblio-top-mask" aria-hidden="true" />
                  <table
                    ref={tableRef}
                    className="tabla-documentos tabla-documentos--bibliografia doc-biblio-table-grid"
                  >
                    <BibliografiaColgroup />
                    <thead>{theadRow}</thead>
                    <tbody>{tbodyContent}</tbody>
                  </table>
                </div>

                <div className="doc-biblio-mobile">
                  <table className="tabla-documentos tabla-documentos--bibliografia">
                    <colgroup>
                      <col style={{ width: "70px" }} />
                      <col style={{ width: "16%" }} />
                      <col />
                    </colgroup>
                    <thead>{theadRow}</thead>
                    <tbody>{tbodyContent}</tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!hasDocs && (
        <p style={{ textAlign: "center", marginTop: "1rem" }}>
          No hay datos para mostrar en Documentos.
        </p>
      )}
    </>
  );
}

export default VolcanDocumentos;
